import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Paperclip, X, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { chatFileAPI } from '../lib/api';
import { toast } from 'sonner';

const InputComment = forwardRef(({ members = [], onChange }, ref) => {
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [tempMentions, setTempMentions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);


  useImperativeHandle(ref, () => ({
    clearInput: () => {
      setComment("");
      setOpen(false);
      setQuery("");
      setActiveIndex(-1);
      setAttachments([]);
      if (onChange) {
        onChange({
          plainText: "",
          mentions: [],
          rawText: "",
          attachments: [],
        });
      }
    },
  }));
  
const getUniqueMentions = (mentions) => {
  const seen = new Set();
  return mentions.filter((m) => {
    if (seen.has(m.user_id)) {
      return false;
    }
    seen.add(m.user_id);
    return true;
  });
};


  // Ubah mention jadi @user_id
  
// Ubah mention jadi @user_id
const convertToRawText = (text) => {
  let raw = text;

  // filter mention yang masih ada di text
  const validMentions = tempMentions.filter((m) => text.includes(m.query));

  // replace mention yang valid
  validMentions.forEach((m) => {
    raw = raw.replace(new RegExp(m.query, "gi"), `@${m.user_id}`);
  });

  // update state tempMentions agar hanya menyimpan yang valid
  if (validMentions.length !== tempMentions.length) {
    setTempMentions(validMentions);
  }
  console.log(tempMentions);
  return raw;
};



  const handleChange = (e) => {
    const value = e.target.value;
    setComment(value);

    const cursorPos = e.target.selectionStart;
    const textUntilCursor = value.slice(0, cursorPos);
    const atIndex = textUntilCursor.lastIndexOf("@");

    if (atIndex !== -1) {
      const q = textUntilCursor.slice(atIndex + 1);
      const validChar = /^[a-zA-Z0-9]+$/;
      if (q.length > 0 && validChar.test(q)) {
        setQuery(q);
        setOpen(true);
      } else {
        setOpen(false);
        setQuery("");
      }
    } else {
      setOpen(false);
      setQuery("");
    }

    const rawText = convertToRawText(value);

    if (onChange) {
      onChange({
        plainText: value,
        mentions: getUniqueMentions(tempMentions),
        rawText,
        attachments,
      });
    }
  };

const handleSelectMember = (member) => {
  if (!textareaRef.current) return;
  const cursorPos = textareaRef.current.selectionStart;
  const textUntilCursor = comment.slice(0, cursorPos);
  const atIndex = textUntilCursor.lastIndexOf("@");

  const beforeAt = comment.slice(0, atIndex);
  const afterCursor = comment.slice(cursorPos);

  const newQuery = `@${member.user_name}`;
  const newText = `${beforeAt}${newQuery} ${afterCursor}`;

  // Simpan mention sementara
  setTempMentions((prev) => [
    ...prev,
    { query: newQuery, user_id: member.user_id },
  ]);

  setComment(newText);
  setOpen(false);
  setQuery("");
  setActiveIndex(-1);

  const rawText = convertToRawText(newText);

  if (onChange) {
    onChange({
      plainText: newText,
      mentions: tempMentions.concat({ query: newQuery, user_id: member.user_id }),
      rawText,
    });
  }
};


  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.code === "Space") {
      const cursorPos = textareaRef.current.selectionStart;
      const textUntilCursor = comment.slice(0, cursorPos);
      const words = textUntilCursor.split(/\s+/);
      const lastWord = words[words.length - 1];

      if (lastWord.startsWith("@")) {
        const q = lastWord.slice(1);
        const validChar = /^[a-zA-Z0-9]+$/;
        if (q.length > 0 && validChar.test(q)) {
          setQuery(q);
          setOpen(true);
          setActiveIndex(-1);
        }
      }
      return;
    }

    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < filteredMembers.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : filteredMembers.length - 1
      );
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelectMember(filteredMembers[activeIndex]);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const { data } = await chatFileAPI.upload(file);
        return {
          file_url: data.url,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      const newAttachments = [...attachments, ...uploadedFiles];
      setAttachments(newAttachments);

      if (onChange) {
        onChange({
          plainText: comment,
          mentions: getUniqueMentions(tempMentions),
          rawText: convertToRawText(comment),
          attachments: newAttachments,
        });
      }

      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);

    if (onChange) {
      onChange({
        plainText: comment,
        mentions: getUniqueMentions(tempMentions),
        rawText: convertToRawText(comment),
        attachments: newAttachments,
      });
    }
  };

  const filteredMembers = members.filter((m) =>
    m?.user_name?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="w-full">
      {/* Attachments Preview - DI ATAS textarea */}
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5 p-2 bg-gray-50 border border-gray-200 rounded-lg">
          {attachments.map((file, index) => (
            <div 
              key={index} 
              className="inline-flex items-center gap-1.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1 text-xs transition-colors group"
            >
              {file.file_type?.startsWith('image/') ? (
                <ImageIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              ) : (
                <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              )}
              <span className="truncate max-w-[150px] text-gray-700 font-medium">{file.file_name}</span>
              <button
                type="button"
                onClick={() => removeAttachment(index)}
                className="ml-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full p-0.5 transition-colors flex-shrink-0"
                title="Remove file"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div className="relative">
        <Popover.Root open={open}>
          <Popover.Trigger asChild>
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={comment}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Write a comment... (use '@' to mention)"
                rows={2}
                className="resize-none text-sm w-full border border-gray-300 rounded-lg p-3 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {/* Action button - HANYA paperclip di kanan atas */}
              <div className="absolute right-2 top-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  title="Attach file"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Popover.Trigger>
          <Popover.Content
            className="bg-white border rounded shadow-md p-2 text-sm max-w-xs z-50"
            side="top"
            autoFocus={false}
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            {filteredMembers.length > 0 ? (
              filteredMembers.map((m, idx) => (
                <div
                  key={m.user_id}
                  className={`cursor-pointer px-2 py-1 rounded ${
                    idx === activeIndex ? "bg-blue-100" : "hover:bg-gray-100"
                  }`}
                  onClick={() => handleSelectMember(m)}
                >
                  @{m.user_name} <span className="text-gray-400">({m.role})</span>
                </div>
              ))
            ) : (
              <div className="text-gray-400">No matches</div>
            )}
          </Popover.Content>
        </Popover.Root>
      </div>
      
      {/* Upload status */}
      {uploading && (
        <div className="mt-2 text-xs text-blue-600 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Uploading files...
        </div>
      )}
    </div>
  );
});

export default InputComment;